import React, { useEffect, useState } from 'react';
import { UserPlus, Check, Send, Loader2 } from 'lucide-react';
import type { FriendSummary } from '../../shared/protocol';
import { Avatar } from './Avatar';
import { realtime } from '../lib/realtime';

/**
 * Lista de amigos para marcar a quién invitar. Los conectados van primero y
 * son los únicos que se pueden marcar: la invitación es un aviso en vivo, a
 * quien no tiene la aplicación abierta no le llegaría.
 */
export const InviteFriendsPicker: React.FC<{
  friends: FriendSummary[] | null;
  selected: string[];
  onChange: (ids: string[]) => void;
  /** Ids a los que no tiene sentido invitar (ya están en la sala). */
  exclude?: string[];
  onManageFriends?: () => void;
}> = ({ friends, selected, onChange, exclude = [], onManageFriends }) => {
  if (friends === null) return <div className="h-12 shimmer rounded-lg" />;

  const list = friends
    .filter((f) => !exclude.includes(f.id))
    .sort((a, b) => Number(b.online) - Number(a.online) || a.username.localeCompare(b.username));

  if (list.length === 0) {
    return (
      <p className="text-xs text-ink-500">
        {friends.length === 0 ? 'Aún no tienes amigos agregados.' : 'Tus amigos ya están en la sala.'}{' '}
        {onManageFriends && (
          <button type="button" onClick={onManageFriends} className="text-sky-400 hover:text-sky-300 font-medium">
            Agregar amigos
          </button>
        )}
      </p>
    );
  }

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const online = list.filter((f) => f.online);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
        {list.map((f) => {
          const on = selected.includes(f.id);
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => toggle(f.id)}
              disabled={!f.online}
              aria-pressed={on}
              title={f.online ? (f.inRoom ? 'Está en otra partida' : 'Conectado') : 'Desconectado: no le llegaría'}
              className={`flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border text-sm transition-colors ${
                on
                  ? 'border-sky-500 bg-sky-500/15 text-ink-50'
                  : 'border-ink-700 bg-ink-850 text-ink-300 hover:border-ink-600'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              <span className="relative">
                <Avatar config={f.avatar as never} size={26} className="shadow-none" />
                <span
                  className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-ink-900 ${
                    f.online ? 'bg-success-500' : 'bg-ink-600'
                  }`}
                  aria-hidden="true"
                />
              </span>
              <span className="max-w-[8rem] truncate">{f.username}</span>
              {on && <Check className="w-3.5 h-3.5 text-sky-400" />}
            </button>
          );
        })}
      </div>
      {online.length < list.length && (
        <p className="text-[0.6875rem] text-ink-500">
          {online.length === 0
            ? 'Ninguno está conectado ahora: la invitación solo llega a quien tiene Open Dahu abierto.'
            : 'Los que salen en gris no están conectados: la invitación no les llegaría.'}
        </p>
      )}
      {online.length > 1 && (
        <button
          type="button"
          onClick={() =>
            onChange(selected.length === online.length ? [] : online.map((f) => f.id))
          }
          className="text-xs text-sky-400 hover:text-sky-300 font-medium"
        >
          {selected.length === online.length ? 'Quitar a todos' : `Marcar a los ${online.length} conectados`}
        </button>
      )}
    </div>
  );
};

/** Dentro de la sala: marcar amigos y enviarles la invitación al momento. */
export const InviteFriendsPanel: React.FC<{
  friends: FriendSummary[] | null;
  inRoomIds: string[];
  onManageFriends?: () => void;
}> = ({ friends, inRoomIds, onManageFriends }) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  useEffect(
    () =>
      realtime.on((msg) => {
        if (msg.t !== 'room:invited') return;
        setSending(false);
        setSelected([]);
        setResult(
          msg.sent === 0
            ? 'No le llegó a nadie: quizá se desconectaron.'
            : msg.sent === 1
              ? 'Invitación enviada.'
              : `Invitación enviada a ${msg.sent} amigos.`,
        );
      }),
    [],
  );

  if (!open) {
    return (
      <div className="flex justify-center">
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-sky-700 bg-sky-500/10 text-sky-400 hover:bg-sky-500/20 transition-colors"
        >
          <UserPlus className="w-4 h-4" />
          Invitar a amigos a unirse
        </button>
      </div>
    );
  }

  return (
    <div className="surface p-4 space-y-3 max-w-2xl mx-auto">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-ink-100 flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-sky-400" />
          Invitar a amigos
        </h3>
        <button onClick={() => setOpen(false)} className="text-xs text-ink-500 hover:text-ink-200">
          Cerrar
        </button>
      </div>
      <InviteFriendsPicker
        friends={friends}
        selected={selected}
        onChange={(ids) => {
          setSelected(ids);
          setResult(null);
        }}
        exclude={inRoomIds}
        onManageFriends={onManageFriends}
      />
      <div className="flex items-center gap-3">
        <button
          onClick={() => {
            setSending(true);
            realtime.send({ t: 'room:invite', friendIds: selected });
          }}
          disabled={selected.length === 0 || sending}
          className="flex items-center gap-2 px-4 h-10 rounded-lg font-semibold text-sm bg-sky-600 hover:bg-sky-500 text-white disabled:bg-ink-800 disabled:text-ink-500 transition-colors"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Enviar invitación
        </button>
        {result && <p className="text-xs text-ink-400">{result}</p>}
      </div>
    </div>
  );
};
